<script>
  import { get, call } from 'vuex-pathify'
  import clone from 'clone-deep'
  import AddressForm from '@/components/account/AddressForm'
  import BillingCreditCardForm from '@/components/account/BillingCreditCardForm'
  import LoginForm from '@/components/account/LoginForm'
  import CartItemList from '@/components/checkout/CartItemList'
  import { getCreditCardToken } from '@/helpers/recurly'
  import screen from '@/helpers/screen'
  import addressesApi from '@/api/addresses'

  export default {
    components: {
      AddressForm,
      BillingCreditCardForm,
      LoginForm,
      CartItemList,
    },
    data() {
      return {
        addressFormRef: null,
        billingFormRef: null,
        accountFormRef: null,
        billingSameAsShipping: false,
        checkoutErrorMessage: null,
        recurlyPayload: null,
        placingOrder: false,
      }
    },
    computed: {
      screen,
      ...get('account', ['loggedIn', 'addresses', 'billingInfo']),
      ...get('marketplaceCart', [
        'finalLineItems',
        'isCartEmpty',
        'cartQuantity',
        'subtotal',
        'cartSavings',
        'calculatingLineItems',
        'cartPreview',
        'cartTotal',
        'errors',
      ]),
      checkoutButtonDisabled() {
        return this.placingOrder || this.isCartEmpty || this.calculatingLineItems
      },
    },
    mounted() {
      if (this.isCartEmpty) {
        this.$navigate({ name: 'MarketplaceCart' })
      }
    },
    methods: {
      ...call('account', ['signup']),
      clearErrors() {
        this.checkoutErrorMessage = null
        this.$store.dispatch('marketplaceCart/clearErrors')
      },
      savePaymentInfo(payload) {
        this.recurlyPayload = payload
      },
      async preview() {
        // For logged-in users with existing addresses, preview is triggered automatically
        // For guests, shipping/tax are calculated after account creation during checkout
      },
      errorHandler(error) {
        if (error.data?.errors?.username?.includes('has already been taken')) {
          this.checkoutErrorMessage =
            'This email address is already associated with an existing account.'
        } else if (error.data?.errors?.password) {
          this.checkoutErrorMessage = 'Invalid password'
        } else if (error.data?.errors?.shippingAddress) {
          this.checkoutErrorMessage =
            "Oops, there appears to be an error with your shipping address. If you're using a form autofill tool, try entering each field manually."
        } else {
          this.checkoutErrorMessage = error.message ?? 'An unexpected error occurred.'
        }
      },
      async placeOrderHandler() {
        try {
          this.checkoutErrorMessage = null
          this.placingOrder = true

          // 1. Validate account form (for guests)
          let accountValidated = true
          if (!this.loggedIn) {
            accountValidated = await this.accountFormRef?.formRef.validate()
          }

          // 2. Validate billing form and get payment data
          const billingValidated = await this.billingFormRef?.processForm()
          if (billingValidated && !this.recurlyPayload) {
            // processForm only auto-emits in subscribe flow, so manually trigger submit
            if (this.billingSameAsShipping) {
              // BillingCreditCardForm.submit() reads from subscribe store for billingSameAsShipping,
              // which is empty in market checkout context. Build payload from our address form instead.
              const address = this.addressFormRef.address
              const countries = this.$store.get('config/countries') || []
              const countryIso = countries.find((c) => c.id === address.countryId)?.iso
              this.recurlyPayload = {
                first_name: address.firstName,
                last_name: address.lastName,
                postal_code: address.zipCode,
                number: this.billingFormRef.card.number,
                month: this.billingFormRef.expirationDateInputRef.month,
                year: this.billingFormRef.expirationDateInputRef.year,
                cvv: this.billingFormRef.card.securityCode,
                country: countryIso,
              }
            } else {
              this.billingFormRef.submit()
            }
          }

          // 3. Validate address form
          const addressValidated = await this.addressFormRef?.formRef.validate()

          if (!accountValidated || !billingValidated || !addressValidated) {
            this.checkoutErrorMessage = 'Please fill out all required fields.'
            return
          }

          this.$store.set('general/navigationLoading', true)

          // 4. Create account if guest
          if (!this.loggedIn) {
            const credentials = this.$store.get('account/rememberedCredentials')
            await this.signup({ username: credentials.username, password: credentials.password })
          }

          // 5. Save shipping address
          const address = clone(this.addressFormRef.address)
          const savedAddress = await addressesApi.createAddress(address)

          // Refresh account data to get new address
          this.$store.set('account/markAsStale')
          await this.$store.dispatch('account/ensureFresh')

          // 6. Set shipping address in cart
          this.$store.set('marketplaceCart/selectedShippingAddressId', savedAddress.id)

          // 7. Get Recurly token and save billing info
          if (this.recurlyPayload) {
            const token = await getCreditCardToken(this.recurlyPayload)
            await this.$store.dispatch('account/updateBillingInfo', token)
          }

          // 8. Review order (calculates shipping, tax, total)
          await this.$store.dispatch('marketplaceCart/reviewOrder')

          // 9. Place order
          await this.$store.dispatch('marketplaceCart/placeOrder')

          if (this.$store.get('marketplaceCart/successfulOrder')) {
            this.$navigate({
              name: 'CheckoutConfirmation',
              params: { storeType: 'marketplace' },
            })
          }
        } catch (error) {
          this.errorHandler(error)
        } finally {
          this.placingOrder = false
          this.$store.set('general/navigationLoading', false)
        }
      },
    },
  }
</script>

<template>
  <div class="pt-4 pb-8 md:py-10 bg-dawn-lt3 min-h-screen">
    <BaseContainer max-width="xl" class="pb-14 lg:pb-40">
      <div class="mt-6 mx-4 lg:mx-0 flex flex-col lg:flex-row lg:justify-center">
        <!-- Left column: Forms -->
        <div class="w-full lg:max-w-140">
          <!-- Cart summary -->
          <div class="mb-6">
            <h3 class="font-semibold text-base lg:text-lg leading-close mb-4">
              Your Items ({{ cartQuantity }})
            </h3>
            <CartItemList
              v-if="!isCartEmpty"
              :line-items="finalLineItems"
            />
            <div v-if="isCartEmpty" class="text-center py-8">
              <p class="text-gray-dark">Your cart is empty.</p>
              <BaseButton :to="{ name: 'MarketProductList' }" class="mt-4">
                Shop Products
              </BaseButton>
            </div>
          </div>

          <BaseDivider color="black" :weight="screen.lg ? 1 : 2" class="mb-6" />

          <!-- Shipping form -->
          <div class="px-4 flex flex-col">
            <div class="flex flex-col items-center lg:items-start mb-6 lg:mb-2">
              <h3 class="font-semibold text-base lg:text-lg leading-close">Shipping</h3>
              <p class="mt-1 text-sm">Tell us where you'd like your order shipped.</p>
            </div>
            <AddressForm
              v-ref="addressFormRef"
              hide-button
              :select-option="loggedIn && Boolean(addresses.length)"
              @focus="clearErrors"
              @blurAddressInput="preview"
            />
            <div class="flex justify-center lg:justify-start">
              <BaseCheckbox
                v-model="billingSameAsShipping"
                vertical-alignment="center"
                class="mt-6"
              >
                <span class="font-semibold text-xs">Billing address: Same as shipping</span>
              </BaseCheckbox>
            </div>
            <BaseDivider
              color="black"
              :weight="screen.lg ? 1 : 2"
              :class="`${
                screen.lg ? 'w-full' : 'w-screen -ml-8'
              } mt-6 lg:mt-18px mb-18px lg:mb-26px`"
            />
          </div>

          <!-- Payment form -->
          <div class="px-4 flex flex-col">
            <div class="mb-5 text-center lg:text-left">
              <h3 class="font-semibold text-base lg:text-lg leading-close">Payment</h3>
              <span class="mt-2 block">
                <BaseCreditCards />
              </span>
            </div>
            <BillingCreditCardForm
              v-ref="billingFormRef"
              hide-button
              :billing-same-as-shipping="billingSameAsShipping"
              selected-payment-method="credit-card"
              @focus="clearErrors"
              @blur="clearErrors"
              @submit="savePaymentInfo"
            />
            <BaseDivider
              color="black"
              :weight="screen.lg ? 1 : 2"
              :class="`${
                screen.lg ? 'w-full' : 'w-screen -ml-8'
              } mt-6 lg:mt-18px mb-18px lg:mb-26px`"
            />
          </div>

          <!-- Account Info (guests only) -->
          <div v-if="!loggedIn" data-cy="market-checkout-account-section" class="px-4 flex flex-col">
            <div
              class="mb-5 lg:mb-3 text-center lg:text-left flex flex-col items-center lg:items-start"
            >
              <h3 class="font-semibold text-base lg:text-lg leading-close">Account Info</h3>
              <p class="mt-1 text-sm">
                Enter your email and set a password to create your account.
              </p>
            </div>
            <div class="-mt-10">
              <LoginForm
                v-ref="accountFormRef"
                hide-button
                fields-on-one-line
                @focus="clearErrors"
              />
            </div>
            <div class="mt-2">
              Already have an account? Log in
              <BaseLinkStyled
                :to="{ name: 'AccountLogin', query: { returnPath: '/market/checkout' } }"
                inline
              >
                here
              </BaseLinkStyled>
            </div>
            <BaseDivider
              color="black"
              :weight="screen.lg ? 1 : 2"
              :class="`${
                screen.lg ? 'w-full' : 'w-screen -ml-8'
              } mt-6 lg:mt-18px mb-10px lg:mb-26px`"
            />
          </div>
        </div>

        <!-- Right column: Order Summary -->
        <div class="lg:max-w-90 lg:ml-4 w-full">
          <div class="lg:border lg:border-black rounded-md mb-3 pb-4 flex flex-col">
            <div class="px-6">
              <div class="font-semibold text-center lg:text-left mb-4 lg:mb-0 lg:mt-15px">
                Order Summary
              </div>
              <BasePriceBreakdown class="my-2">
                <BasePanelWarning v-if="checkoutErrorMessage" class="mb-6">
                  {{ checkoutErrorMessage }}
                </BasePanelWarning>
                <BasePriceBreakdownItem
                  label="Subtotal"
                  :price="subtotal"
                />
                <BasePriceBreakdownItem
                  v-if="cartSavings > 0"
                  label="You save"
                  :price="-cartSavings"
                />
                <BasePriceBreakdownItem
                  label="Shipping"
                  :sublabel="!cartPreview ? 'Enter your shipping address' : undefined"
                  :price="cartPreview ? (cartPreview.shipping || 0) / 100 : undefined"
                  :calculating="calculatingLineItems"
                />
                <BasePriceBreakdownItem
                  label="Estimated Tax"
                  :sublabel="!cartPreview ? 'Enter your shipping address' : undefined"
                  :price="cartPreview ? (cartPreview.tax || 0) / 100 : undefined"
                  :calculating="calculatingLineItems"
                />
                <BaseDivider />
                <template v-slot:total>
                  <BasePriceBreakdownItem
                    label="Total"
                    :price="cartTotal !== null && cartTotal !== undefined ? cartTotal : subtotal"
                    :calculating="calculatingLineItems"
                  />
                </template>
              </BasePriceBreakdown>
              <BaseButton
                :disabled="checkoutButtonDisabled"
                @click="placeOrderHandler"
              >
                <span v-if="placingOrder">Placing order...</span>
                <span v-else>Place Order</span>
              </BaseButton>
              <p class="mt-4 text-3xs">
                By clicking "Place Order" you agree to the Alltrue
                <BaseLinkStyled color="black" :to="{ name: 'PageTermsOfUse' }" target="_blank">
                  Terms of Use
                </BaseLinkStyled>
                and
                <BaseLinkStyled color="black" :to="{ name: 'PagePrivacy' }" target="_blank">
                  Privacy Policy </BaseLinkStyled
                >.
              </p>
            </div>
          </div>
        </div>
      </div>
    </BaseContainer>
  </div>
</template>
